/**
 * QGIS-style raster Information fields for the viewer.
 *
 * Mirrors the Layer Properties → Information / Source summary operators get
 * in QGIS: acquisition date, resolution (pixel size), CRS, band count, pixel
 * type (GDAL dtype), data source path, and extent coverage. Values prefer the
 * live TiTiler `/cog/info` header (what QGIS reads from the file) and fall
 * back to layer / converter metadata when the COG is not reachable.
 */

import type { Layer } from '@geosoft/viewer-sdk';
import {
  bboxAreaSquareMeters,
  formatArea,
} from '@/app/layerMetadata';
import { extractCogFileUrl } from '@/modules/layer-diagnostics/outputFiles';
import type { TitilerRasterProfile } from '@/utils/titilerRasterProfile';

export type RasterMetadataRow = {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  /** Prefer mono for CRS codes, paths, dtypes, numeric extents. */
  readonly mono?: boolean;
};

export type RasterMetadataView = {
  readonly rows: readonly RasterMetadataRow[];
  /** True when at least one row came from a live `/cog/info` read. */
  readonly fromTitiler: boolean;
};

type LayerLike = Pick<
  Layer,
  'id' | 'label' | 'crs' | 'bbox' | 'temporal' | 'endpoints' | 'style' | 'sourceCrs'
> & {
  readonly kind?: string;
};

function styleRecord(layer: LayerLike | null | undefined): Record<string, unknown> {
  if (!layer?.style || typeof layer.style !== 'object') return {};
  return layer.style as Record<string, unknown>;
}

function intakeManifest(layer: LayerLike | null | undefined): Record<string, unknown> {
  const style = styleRecord(layer);
  const manifest = style.intakeManifest;
  return manifest && typeof manifest === 'object' && !Array.isArray(manifest)
    ? (manifest as Record<string, unknown>)
    : {};
}

function firstString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return null;
}

/** Collapse OGC CRS URIs / URN forms to the EPSG:nnnn QGIS shows. */
export function formatCrsCode(crs: string | null | undefined): string | null {
  if (!crs || !crs.trim()) return null;
  const raw = crs.trim();
  const epsg = /EPSG[/:](?:0\/)?(\d+)/i.exec(raw);
  if (epsg) return `EPSG:${epsg[1]}`;
  const urn = /urn:ogc:def:crs:EPSG::(\d+)/i.exec(raw);
  if (urn) return `EPSG:${urn[1]}`;
  return raw;
}

/** QGIS-style GDAL data-type label. */
export function formatPixelType(dtype: string | null | undefined): string | null {
  if (!dtype || !dtype.trim()) return null;
  const key = dtype.trim().toLowerCase();
  const labels: Record<string, string> = {
    uint8: 'Byte (8-bit unsigned integer)',
    int8: 'Int8 (8-bit signed integer)',
    uint16: 'UInt16 (16-bit unsigned integer)',
    int16: 'Int16 (16-bit signed integer)',
    uint32: 'UInt32 (32-bit unsigned integer)',
    int32: 'Int32 (32-bit signed integer)',
    float32: 'Float32 (32-bit floating point)',
    float64: 'Float64 (64-bit floating point)',
    cint16: 'CInt16 (complex)',
    cint32: 'CInt32 (complex)',
    cfloat32: 'CFloat32 (complex)',
    cfloat64: 'CFloat64 (complex)',
  };
  return labels[key] ?? dtype;
}

/** Basename (or last path segment) for a COG /vsis3/ / file URL. */
export function formatDataSourcePath(path: string | null | undefined): string | null {
  if (!path || !path.trim()) return null;
  let decoded = path.trim();
  try {
    decoded = decodeURIComponent(decoded);
  } catch {
    // keep raw
  }
  // Strip scheme /vsi prefixes so the operator sees the object key / filename.
  const cleaned = decoded
    .replace(/^\/vsis3\//i, '')
    .replace(/^s3:\/\//i, '')
    .replace(/^file:\/\//i, '');
  const parts = cleaned.split(/[/\\]/).filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1] : cleaned;
}

function formatCoord(value: number, geographic: boolean): string {
  if (geographic) {
    return Math.abs(value) >= 10 ? value.toFixed(5) : value.toFixed(6);
  }
  if (Math.abs(value) >= 1000) return value.toFixed(2);
  return value.toFixed(3);
}

/**
 * Pixel size from native bounds ÷ dimensions — what QGIS shows as
 * "Pixel Size" when the geotransform is axis-aligned.
 */
export function formatPixelResolution(
  profile: Pick<TitilerRasterProfile, 'width' | 'height' | 'bounds' | 'crs'> | null,
): string | null {
  if (!profile?.width || !profile.height || !profile.bounds) return null;
  if (profile.width <= 0 || profile.height <= 0) return null;
  const [minX, minY, maxX, maxY] = profile.bounds;
  const dx = Math.abs(maxX - minX) / profile.width;
  const dy = Math.abs(maxY - minY) / profile.height;
  if (!Number.isFinite(dx) || !Number.isFinite(dy) || dx <= 0 || dy <= 0) return null;

  const crs = formatCrsCode(profile.crs) ?? '';
  const geographic = /4326|4269|CRS84|WGS\s*84/i.test(crs) || Math.abs(maxX) <= 180;
  if (geographic) {
    const same = Math.abs(dx - dy) / Math.max(dx, dy) < 0.01;
    return same
      ? `${dx.toPrecision(4)} °`
      : `${dx.toPrecision(4)} ° × ${dy.toPrecision(4)} °`;
  }
  const same = Math.abs(dx - dy) / Math.max(dx, dy) < 0.01;
  return same
    ? `${trimResolution(dx)} m`
    : `${trimResolution(dx)} m × ${trimResolution(dy)} m`;
}

function trimResolution(metres: number): string {
  if (metres >= 100) return metres.toFixed(1);
  if (metres >= 1) return metres.toFixed(2);
  if (metres >= 0.01) return metres.toFixed(3);
  return metres.toPrecision(3);
}

export function formatExtentCoverage(
  bounds: readonly [number, number, number, number] | null | undefined,
  crs: string | null | undefined,
  fallbackBbox?: readonly number[] | null,
): string | null {
  const box = bounds ?? (
    fallbackBbox && fallbackBbox.length >= 4
      ? [fallbackBbox[0], fallbackBbox[1], fallbackBbox[2], fallbackBbox[3]] as const
      : null
  );
  if (!box) return null;
  const [minX, minY, maxX, maxY] = box;
  const crsCode = formatCrsCode(crs);
  const geographic = /4326|4269|CRS84|WGS\s*84/i.test(crsCode ?? '')
    || (Math.abs(minX) <= 180 && Math.abs(maxX) <= 180 && Math.abs(minY) <= 90);
  const unitHint = geographic ? '°' : (crsCode?.startsWith('EPSG:') ? '' : '');
  const extent = geographic
    ? `${formatCoord(minX, true)}, ${formatCoord(minY, true)} — ${formatCoord(maxX, true)}, ${formatCoord(maxY, true)}${unitHint ? ` ${unitHint}` : ''}`
    : `${formatCoord(minX, false)}, ${formatCoord(minY, false)} — ${formatCoord(maxX, false)}, ${formatCoord(maxY, false)}`;

  // Area is only meaningful for lon/lat or projected metres.
  const area = formatArea(bboxAreaSquareMeters(
    geographic ? box : (Math.abs(minX) > 200 || Math.abs(maxX) > 200 ? box : null),
  ));
  if (area !== '—') return `${extent} (${area})`;
  return extent;
}

function formatAcquisitionDate(layer: LayerLike | null | undefined): string | null {
  const temporal = layer?.temporal;
  const style = styleRecord(layer);
  const manifest = intakeManifest(layer);
  const iso = firstString(
    temporal?.end,
    temporal?.start,
    style.acquisitionDate,
    style.lastTiledAt,
    style.captureDate,
    manifest.acquisitionDate,
    manifest.captureDate,
    manifest.date,
  );
  if (!iso) return null;
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return iso;
  try {
    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      timeZone: 'UTC',
    }).format(new Date(parsed));
  } catch {
    return iso;
  }
}

function resolveDataSource(
  layer: LayerLike | null | undefined,
  tilesUrl: string | null,
): string | null {
  const style = styleRecord(layer);
  const fromTiles = formatDataSourcePath(
    extractCogFileUrl(tilesUrl ?? undefined)
      ?? extractCogFileUrl(layer?.endpoints?.tiles)
      ?? extractCogFileUrl(layer?.endpoints?.terrain),
  );
  if (fromTiles) return fromTiles;
  return formatDataSourcePath(
    firstString(style.sourceArtifact, style.sourcePath, style.sourceFile, layer?.label),
  );
}

function pushRow(
  rows: RasterMetadataRow[],
  id: string,
  label: string,
  value: string | null | undefined,
  mono = true,
): void {
  if (!value || !value.trim()) return;
  rows.push({ id, label, value: value.trim(), mono });
}

/**
 * Build the Information-tab rows for a raster / terrain COG.
 * Pass `profile` from {@link fetchTitilerRasterProfile} when available.
 */
export function buildRasterMetadataView(
  layer: LayerLike | null | undefined,
  profile: TitilerRasterProfile | null,
): RasterMetadataView {
  const rows: RasterMetadataRow[] = [];
  const tilesUrl = layer?.endpoints?.tiles ?? layer?.endpoints?.terrain ?? null;
  const fromTitiler = Boolean(profile);

  pushRow(rows, 'acquisition', 'Acquisition date', formatAcquisitionDate(layer), true);

  pushRow(
    rows,
    'resolution',
    'Resolution',
    formatPixelResolution(profile)
      ?? firstString(
        styleRecord(layer).pixelSize,
        styleRecord(layer).resolution,
        intakeManifest(layer).pixelSize,
        intakeManifest(layer).resolution,
      ),
  );

  pushRow(
    rows,
    'crs',
    'CRS',
    formatCrsCode(profile?.crs)
      ?? formatCrsCode(layer?.sourceCrs)
      ?? formatCrsCode(layer?.crs)
      ?? formatCrsCode(firstString(intakeManifest(layer).crs, intakeManifest(layer).crsOut)),
  );

  const bandCount = profile && profile.bandCount > 0
    ? String(profile.bandCount)
    : null;
  pushRow(rows, 'bands', 'Number of bands', bandCount);

  pushRow(rows, 'pixel-type', 'Pixel type', formatPixelType(profile?.dtype ?? null));

  pushRow(rows, 'data-source', 'Data source', resolveDataSource(layer, tilesUrl));

  pushRow(
    rows,
    'extent',
    'Extent coverage',
    formatExtentCoverage(profile?.bounds ?? null, profile?.crs ?? layer?.crs, layer?.bbox),
  );

  // Helpful extras that still match QGIS Information (dimensions / nodata).
  if (profile?.width && profile.height) {
    pushRow(rows, 'dimensions', 'Dimensions', `${profile.width} × ${profile.height} px`);
  }
  if (profile?.nodataValue != null) {
    pushRow(rows, 'nodata', 'No-data value', String(profile.nodataValue));
  }

  return { rows, fromTitiler };
}

/** True when this layer can show a raster Information section. */
export function isRasterMetadataLayer(layer: LayerLike | null | undefined): boolean {
  if (!layer) return false;
  const kind = String(layer.kind ?? '').toLowerCase();
  if (kind === 'raster' || kind === 'terrain') return true;
  const tiles = layer.endpoints?.tiles ?? layer.endpoints?.terrain;
  return typeof tiles === 'string' && tiles.includes('/cog/');
}
